import { onUnmounted, watch, type Ref } from 'vue'
import { HEARTBEAT_URL, reportHeartbeat, type ReportHeartbeatRequest, type WatchedRange } from '@/api/visitor/playback'
import type { PlayState } from '../../types/player'
import { addWatchedRange } from './watchedRanges'

const REPORT_INTERVAL_MS = 10_000
// Don't report trivial plays; mirrors the backend persistence floor.
const MIN_POSITION_SECONDS = 5
// Ignore currentTime jumps larger than this (seeks) when accumulating watch time and
// the watched stretches.
const MAX_TICK_DELTA_SECONDS = 2

type HeartbeatEvent = 'heartbeat' | 'pause' | 'ended'

interface UsePlaybackHeartbeatOptions {
  /**
   * Signed playback token from the embed/player response. The heartbeat is
   * authorized by this token (same model as the playback-info request), and the
   * viewer a resume point is kept for is the one the token carries — an in-app
   * token carries none, so it records nothing. The reporter stays inert until a
   * token + asset id are present.
   */
  playbackToken: Ref<string | undefined>
  /** Asset currently playing (undefined in direct-src mode). */
  assetId: Ref<string | undefined>
  /** Analytics session id from the playback info (omitted ⇒ no analytics sink). */
  sessionId: Ref<string | undefined>
  currentTime: Ref<number>
  duration: Ref<number>
  hasStarted: Ref<boolean>
  playState: Ref<PlayState>
}

/**
 * Reports a unified playback heartbeat for analytics + resume ("Continue
 * Watching"), for every viewer (anonymous or authenticated) and every player
 * context. Sends throttled updates while playing, plus on pause, on end, and a
 * final beacon on page unload. The backend records the resume position (keyed by
 * the viewer) on every tick, and the analytics session at most once every thirty
 * seconds and on every pause and end.
 *
 * Active whenever a playback token + asset id are present — there is no
 * per-context tracking flag; the analytics sink is simply skipped when no
 * session id exists (e.g. the in-app preview player).
 *
 * @returns `reportNow` for manual flushes (e.g. before a manual item change).
 */
export function usePlaybackHeartbeat(options: UsePlaybackHeartbeatOptions) {
  const { playbackToken, assetId, sessionId, currentTime, duration, hasStarted, playState } = options

  let watchTimeSeconds = 0
  // The stretches watched, from the same ticks and the same rule as the watch time,
  // so the two never disagree about what counted as watching. Replaced on every
  // change and never mutated, so a payload built from it keeps what it held.
  let watched: WatchedRange[] = []
  let lastTickTime = 0
  let lastReportMs = 0

  function isEnabled(): boolean {
    return Boolean(playbackToken.value && assetId.value)
  }

  function buildPayload(event: HeartbeatEvent): ReportHeartbeatRequest | null {
    if (!playbackToken.value || !assetId.value) return null
    const payload: ReportHeartbeatRequest = {
      playback_token: playbackToken.value,
      asset_id: assetId.value,
      position_seconds: Math.max(0, currentTime.value),
      duration_seconds: Math.max(0, duration.value),
      event_type: event,
      ended: event === 'ended',
    }
    if (sessionId.value) {
      payload.session_id = sessionId.value
      payload.watch_time_seconds = watchTimeSeconds
      // Every stretch since the sitting opened, not those since the last report: the
      // server records them only when its analytics write is due and ORs them into
      // what it holds, so a report it throttles, or a beacon that arrives late or
      // twice, loses nothing.
      payload.watched = watched
    }
    return payload
  }

  async function report(event: HeartbeatEvent = 'heartbeat'): Promise<void> {
    const payload = buildPayload(event)
    if (!payload) return
    lastReportMs = Date.now()
    try {
      await reportHeartbeat(payload)
    } catch {
      // Heartbeat is best-effort; never disrupt playback.
    }
  }

  /** Send a final update via the Beacon API, reliable during page unload. */
  function reportBeacon(event: HeartbeatEvent): void {
    const payload = buildPayload(event)
    if (!payload || typeof navigator.sendBeacon !== 'function') return
    try {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
      navigator.sendBeacon(HEARTBEAT_URL, blob)
    } catch {
      // Ignore — beacon is a best-effort final flush.
    }
  }

  // Accumulate real watch time + throttle periodic reports while playing.
  watch(currentTime, (time) => {
    if (!isEnabled() || !hasStarted.value || playState.value !== 'playing') {
      lastTickTime = time
      return
    }

    const delta = time - lastTickTime
    if (delta > 0 && delta < MAX_TICK_DELTA_SECONDS) {
      watchTimeSeconds += delta
      watched = addWatchedRange(watched, lastTickTime, time)
    }
    lastTickTime = time

    if (time < MIN_POSITION_SECONDS) return
    if (Date.now() - lastReportMs >= REPORT_INTERVAL_MS) {
      report('heartbeat')
    }
  })

  // Report on pause and on natural end.
  watch(playState, (state) => {
    if (!isEnabled()) return
    if (state === 'paused' && currentTime.value >= MIN_POSITION_SECONDS) {
      report('pause')
    } else if (state === 'ended') {
      report('ended')
    }
  })

  // Reset accumulators when switching item within a playlist.
  watch(assetId, () => {
    watchTimeSeconds = 0
    watched = []
    lastTickTime = 0
    lastReportMs = 0
  })

  function flushOnExit(): void {
    if (isEnabled() && hasStarted.value && currentTime.value >= MIN_POSITION_SECONDS) {
      reportBeacon(playState.value === 'ended' ? 'ended' : 'pause')
    }
  }

  function handleVisibilityChange(): void {
    // 'hidden' is the only reliable "page is going away" signal on mobile
    // (tab switch, app backgrounded, imminent swipe-kill), where pagehide /
    // beforeunload frequently don't fire. The Beacon works while the page is
    // still alive, so flushing here captures the watch time that the terminal
    // unload events would otherwise lose.
    if (document.visibilityState === 'hidden') {
      flushOnExit()
    }
  }

  window.addEventListener('pagehide', flushOnExit)
  window.addEventListener('beforeunload', flushOnExit)
  document.addEventListener('visibilitychange', handleVisibilityChange)

  onUnmounted(() => {
    window.removeEventListener('pagehide', flushOnExit)
    window.removeEventListener('beforeunload', flushOnExit)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  return {
    /** Manually flush the current progress (e.g. before a manual item change). */
    reportNow: report,
  }
}
